/**
 * Utility functions for IndexedDB operations related to order queue
 */

interface StoredOrder {
  id: string;
  order_id: string;
  store_id: string;
  type: string;
  status: string;
  timestamp: string;
  [key: string]: any;
}

const ORDER_QUEUE_DB_NAME = 'OrderQueueDB';
const ORDER_QUEUE_DB_VERSION = 2;

function ensureOrderQueueStores(db: IDBDatabase): void {
  if (!db.objectStoreNames.contains('pendingOrders')) {
    const objectStore = db.createObjectStore('pendingOrders', { keyPath: 'id' });
    objectStore.createIndex('order_id', 'order_id', { unique: false });
    objectStore.createIndex('timestamp', 'timestamp', { unique: false });
    console.log('[indexedDBUtils] pendingOrders object store created');
  }

  if (!db.objectStoreNames.contains('appSettings')) {
    db.createObjectStore('appSettings', { keyPath: 'key' });
    console.log('[indexedDBUtils] appSettings object store created');
  }
}

/**
 * Get all stored orders from IndexedDB
 * @returns {Promise<StoredOrder[]>} - Array of stored orders
 */
export async function getStoredOrdersFromIndexedDB(): Promise<StoredOrder[]> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ORDER_QUEUE_DB_NAME, ORDER_QUEUE_DB_VERSION);

    request.onerror = () => {
      console.error('[indexedDBUtils] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      
      // Check if object store exists
      if (!db.objectStoreNames.contains('pendingOrders')) {
        console.log('[indexedDBUtils] No pendingOrders store found');
        resolve([]);
        return;
      }

      const transaction = db.transaction(['pendingOrders'], 'readonly');
      const store = transaction.objectStore('pendingOrders');
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const orders = getAllRequest.result || [];
        console.log(`[indexedDBUtils] Retrieved ${orders.length} stored order(s) from IndexedDB`);
        resolve(orders);
      };

      getAllRequest.onerror = () => {
        console.error('[indexedDBUtils] Failed to get orders:', getAllRequest.error);
        reject(getAllRequest.error);
      };

      transaction.onerror = () => {
        console.error('[indexedDBUtils] Transaction error:', transaction.error);
        reject(transaction.error);
      };
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      ensureOrderQueueStores(db);
    };
  });
}

/**
 * Remove an order from IndexedDB
 * @param {string} orderId - The order ID to remove
 * @returns {Promise<void>} - Promise that resolves when order is removed
 */
export async function removeOrderFromIndexedDB(orderId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ORDER_QUEUE_DB_NAME, ORDER_QUEUE_DB_VERSION);

    request.onerror = () => {
      console.error('[indexedDBUtils] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      
      if (!db.objectStoreNames.contains('pendingOrders')) {
        console.log('[indexedDBUtils] No pendingOrders store found');
        resolve();
        return;
      }

      const transaction = db.transaction(['pendingOrders'], 'readwrite');
      const store = transaction.objectStore('pendingOrders');
      
      // Find order by order_id index
      const index = store.index('order_id');
      const getRequest = index.get(orderId);

      getRequest.onsuccess = () => {
        const order = getRequest.result;
        if (order) {
          const deleteRequest = store.delete(order.id);
          deleteRequest.onsuccess = () => {
            console.log(`[indexedDBUtils] Removed order ${orderId} from IndexedDB`);
            resolve();
          };
          deleteRequest.onerror = () => reject(deleteRequest.error);
        } else {
          console.log(`[indexedDBUtils] Order ${orderId} not found in IndexedDB`);
          resolve();
        }
      };

      getRequest.onerror = () => {
        console.error('[indexedDBUtils] Failed to find order:', getRequest.error);
        reject(getRequest.error);
      };

      transaction.onerror = () => {
        console.error('[indexedDBUtils] Transaction error:', transaction.error);
        reject(transaction.error);
      };
    };
  });
}

/**
 * Clear all stored orders from IndexedDB
 * @returns {Promise<void>} - Promise that resolves when all orders are cleared
 */
export async function clearAllStoredOrders(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ORDER_QUEUE_DB_NAME, ORDER_QUEUE_DB_VERSION);

    request.onerror = () => {
      console.error('[indexedDBUtils] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      
      if (!db.objectStoreNames.contains('pendingOrders')) {
        console.log('[indexedDBUtils] No pendingOrders store found');
        resolve();
        return;
      }

      const transaction = db.transaction(['pendingOrders'], 'readwrite');
      const store = transaction.objectStore('pendingOrders');
      const clearRequest = store.clear();

      clearRequest.onsuccess = () => {
        console.log('[indexedDBUtils] Cleared all orders from IndexedDB');
        resolve();
      };

      clearRequest.onerror = () => {
        console.error('[indexedDBUtils] Failed to clear orders:', clearRequest.error);
        reject(clearRequest.error);
      };

      transaction.onerror = () => {
        console.error('[indexedDBUtils] Transaction error:', transaction.error);
        reject(transaction.error);
      };
    };
  });
}

/**
 * Store ACTIVE_STORE_ID in IndexedDB (for service worker access)
 * Service workers can't access localStorage, so we store it in IndexedDB
 * Also sends a message to the service worker to update immediately
 * @param {string} storeId - The active store ID to store
 * @returns {Promise<void>} - Promise that resolves when store ID is stored
 */
export async function storeActiveStoreId(storeId: string): Promise<void> {
  // ✅ CRITICAL: Also notify service worker immediately via message
  // This ensures service worker has the latest ACTIVE_STORE_ID even if IndexedDB write is delayed
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    try {
      const messageChannel = new MessageChannel();
      const promise = new Promise<void>((resolve, reject) => {
        messageChannel.port1.onmessage = (event) => {
          if (event.data.success) {
            console.log('[indexedDBUtils] Service worker confirmed ACTIVE_STORE_ID update:', storeId);
            resolve();
          } else {
            console.warn('[indexedDBUtils] Service worker failed to update ACTIVE_STORE_ID:', event.data.error);
            // Don't reject - IndexedDB write will still happen
            resolve();
          }
        };
        // Timeout after 1 second - don't block if service worker doesn't respond
        setTimeout(() => {
          console.log('[indexedDBUtils] Service worker message timeout (continuing with IndexedDB write)');
          resolve();
        }, 1000);
      });
      
      navigator.serviceWorker.controller.postMessage(
        { type: 'UPDATE_ACTIVE_STORE_ID', storeId },
        [messageChannel.port2]
      );
      
      // Wait for service worker confirmation (with timeout)
      await promise;
    } catch (error) {
      console.warn('[indexedDBUtils] Failed to send ACTIVE_STORE_ID to service worker:', error);
      // Continue with IndexedDB write anyway
    }
  }
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ORDER_QUEUE_DB_NAME, ORDER_QUEUE_DB_VERSION);

    request.onerror = () => {
      console.error('[indexedDBUtils] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      
      // Create appSettings store if it doesn't exist
      if (!db.objectStoreNames.contains('appSettings')) {
        console.log('[indexedDBUtils] appSettings store not found, will be created on upgrade');
        // Close and reopen with version 2 to trigger upgrade
        db.close();
        const upgradeRequest = indexedDB.open(ORDER_QUEUE_DB_NAME, ORDER_QUEUE_DB_VERSION);
        upgradeRequest.onupgradeneeded = (event) => {
          const upgradeDb = (event.target as IDBOpenDBRequest).result;
          ensureOrderQueueStores(upgradeDb);
        };
        upgradeRequest.onsuccess = () => {
          const upgradeDb = upgradeRequest.result;
          const transaction = upgradeDb.transaction(['appSettings'], 'readwrite');
          const store = transaction.objectStore('appSettings');
          const putRequest = store.put({ key: 'ACTIVE_STORE_ID', value: storeId, updatedAt: new Date().toISOString() });
          putRequest.onsuccess = () => {
            console.log('[indexedDBUtils] Stored ACTIVE_STORE_ID in IndexedDB:', storeId);
            resolve();
          };
          putRequest.onerror = () => reject(putRequest.error);
          transaction.onerror = () => reject(transaction.error);
        };
        upgradeRequest.onerror = () => reject(upgradeRequest.error);
        return;
      }

      const transaction = db.transaction(['appSettings'], 'readwrite');
      const store = transaction.objectStore('appSettings');
      const putRequest = store.put({ key: 'ACTIVE_STORE_ID', value: storeId, updatedAt: new Date().toISOString() });

      putRequest.onsuccess = () => {
        console.log('[indexedDBUtils] Stored ACTIVE_STORE_ID in IndexedDB:', storeId);
        resolve();
      };

      putRequest.onerror = () => {
        console.error('[indexedDBUtils] Failed to store ACTIVE_STORE_ID:', putRequest.error);
        reject(putRequest.error);
      };

      transaction.onerror = () => {
        console.error('[indexedDBUtils] Transaction error:', transaction.error);
        reject(transaction.error);
      };
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      ensureOrderQueueStores(db);
    };
  });
}

/**
 * Get ACTIVE_STORE_ID from IndexedDB (for service worker access)
 * @returns {Promise<string|null>} - Promise that resolves with the active store ID or null
 */
export async function getActiveStoreId(): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ORDER_QUEUE_DB_NAME, ORDER_QUEUE_DB_VERSION);

    request.onerror = () => {
      console.error('[indexedDBUtils] Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;
      
      if (!db.objectStoreNames.contains('appSettings')) {
        console.log('[indexedDBUtils] appSettings store not found');
        resolve(null);
        return;
      }

      const transaction = db.transaction(['appSettings'], 'readonly');
      const store = transaction.objectStore('appSettings');
      const getRequest = store.get('ACTIVE_STORE_ID');

      getRequest.onsuccess = () => {
        const result = getRequest.result;
        const storeId = result ? result.value : null;
        console.log('[indexedDBUtils] Retrieved ACTIVE_STORE_ID from IndexedDB:', storeId);
        resolve(storeId);
      };

      getRequest.onerror = () => {
        console.error('[indexedDBUtils] Failed to get ACTIVE_STORE_ID:', getRequest.error);
        reject(getRequest.error);
      };

      transaction.onerror = () => {
        console.error('[indexedDBUtils] Transaction error:', transaction.error);
        reject(transaction.error);
      };
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      ensureOrderQueueStores(db);
    };
  });
}

